import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import "./themeupdate.css";
import { fetchThemeData } from "../../themeSlice";
import { selectAdminToken, selectOrganizationId, selectSessionId } from "../../sessionSlice";
import { updateThemeData } from "../../functions/updateThemeData";
import { ensureThemeJsonArray, getDvSourceImageUrl } from "../../../functions/themeAssets";

const DEFAULT_DISH = () => ({
  dish_name: "",
  dish_img: "",
  dish_category_name: "",
  dish_layered_img: "",
  type: "positive",
  popup_text: "",
});
const MIN_DISHES_PER_STAGE = 3;
const MAX_DISHES_PER_STAGE = 5;

const MAX_STAGE_TITLE_LEN = 15;
const MAX_STAGE_QUESTION_LEN = 100;
const MAX_POPUP_TEXT_LEN = 60;
const MAX_DISH_TEXT_LEN = 15;

const clampText = (value, max) => {
  const s = value == null ? "" : String(value);
  return s.length > max ? s.slice(0, max) : s;
};

const DEFAULT_ROUND = (index) => ({
  round_id: index + 1,
  title: "",
  question: "",
  hint_image: "",
  required_recipe_image: "",
  sos1_layered_image: "",
  sos2_layered_image: "",
  sos3_layered_image: "",
  dishes: Array.from({ length: MIN_DISHES_PER_STAGE }, () => DEFAULT_DISH()),
});

const apiUrl = (path) => `${process.env.REACT_APP_BACKEND_URL}${path}`;

const pngFileWithBasename = (file, basename) =>
  new File([file], basename, {
    type: file.type || "image/png",
    lastModified: file.lastModified,
  });

/** Expected width × height (px) per upload field — PNG only for all. */
const THEME_IMAGE_RULES = {
  dish_img: { w: 100, h: 100 },
  dish_layered_img: { w: 400, h: 400 },
  hint_image: { w: 400, h: 400 },
  required_recipe_image: { w: 400, h: 400 },
  sos1_layered_image: { w: 400, h: 400 },
  sos2_layered_image: { w: 400, h: 400 },
  sos3_layered_image: { w: 400, h: 400 },
  plate_image: { w: 200, h: 200 },
  bowl_image: { w: 400, h: 400 },
  table_image: { w: 1349, h: 717 },
  sos1_image: { w: 50, h: 100 },
  sos2_image: { w: 50, h: 100 },
  sos3_image: { w: 50, h: 100 },
};

const loadImageDimensions = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read image"));
    };
    img.src = url;
  });

const isPngFile = (file) => {
  const ext = (file.name.split(".").pop() || "").toLowerCase();
  return file.type === "image/png" || ext === "png";
};

/**
 * @returns {{ ok: true } | { ok: false, title?: string, text: string }}
 */
const validateThemeImage = async (file, fieldKey) => {
  if (!file) return { ok: false, text: "Please choose an image to upload." };
  if (!isPngFile(file)) {
    return { ok: false, text: "Please upload a PNG image only." };
  }
  const rule = THEME_IMAGE_RULES[fieldKey];
  if (!rule) return { ok: true };
  try {
    const { width, height } = await loadImageDimensions(file);
    if (width !== rule.w || height !== rule.h) {
      return {
        ok: false,
        text: `Please upload an image with dimensions ${rule.w}x${rule.h} px`,
      };
    }
    return { ok: true };
  } catch {
    return { ok: false, text: "Please upload a valid PNG image." };
  }
};

const swalInvalidImage = (text) => {
  Swal.fire({ icon: "warning", title: "Invalid image", text });
};

/** Red helper text under previews — must match `THEME_IMAGE_RULES` keys used on uploads. */
const ImageSpecHint = ({ fieldKey }) => {
  const rule = THEME_IMAGE_RULES[fieldKey];
  if (!rule) return null;
  return (
    <div className="themeupdate-image-spec-hint" role="note">
      <div>Required format: PNG</div>
      <div>
        Required dimensions: {rule.w}x{rule.h} px
      </div>
    </div>
  );
};

const CharLimitHint = ({ max }) => (
  <div className="themeupdate-char-limit-hint" role="note">
    Maximum {max} characters
  </div>
);

/* ─── Small reusable: image slot ─── */
const ImageSlot = ({ label, src, onFileChange, busy, compact = false, accept = "image/png", fieldKey }) => (
  <div className="dish-img-slot">
    {label && <div className="dish-img-slot-label">{label}</div>}
    <div className={`dish-img-preview ${compact ? "dish-img-preview-compact" : ""}`}>
      {src ? (
        <img src={getDvSourceImageUrl(src)} alt={label} />
      ) : (
        <span className="no-img-icon">🖼️</span>
      )}
    </div>
    {fieldKey ? <ImageSpecHint fieldKey={fieldKey} /> : null}
    <input
      type="file"
      accept={accept}
      disabled={busy}
      onChange={(e) => {
        const input = e.currentTarget;
        onFileChange(input.files?.[0], () => {
          input.value = "";
        });
      }}
    />
  </div>
);

const ThemeUpdate = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const organizationId = useSelector(selectOrganizationId);
  const sessionId = useSelector(selectSessionId);
  const { currentTheme, data, status } = useSelector((state) => state.theme);

  const [loading, setLoading] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [activeTopTab, setActiveTopTab] = useState("stageRounds");
  const [activeRoundTab, setActiveRoundTab] = useState(1);
  const [roundsDraft, setRoundsDraft] = useState([]);

  const postUpdateThemeMedia = useCallback(
    async (file, fieldKey, { uploadOnly = false } = {}) => {
      const formData = new FormData();
      formData.append("themeName", currentTheme || "");
      formData.append("organizationId", organizationId || "admin");
      formData.append("sessionId", sessionId || "admin");
      formData.append(
        "extraData",
        JSON.stringify({
          themeName: data?.themeName ?? data?.newthemeName ?? "",
          themeDescription: data?.themeDescription ?? "",
        })
      );
      formData.append(fieldKey, file);
      if (uploadOnly) formData.append("uploadOnly", "true");
      const res = await fetch(apiUrl("/updateThemeMedia"), {
        method: "POST",
        headers: { Authorization: `Bearer ${adminToken}` },
        body: formData,
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `Upload failed (${res.status})`);
      if (body.success === false && body.error) throw new Error(body.error);
      return body;
    },
    [adminToken, currentTheme, organizationId, sessionId, data]
  );

  useEffect(() => {
    if (currentTheme) dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true }));
  }, [dispatch, currentTheme]);

  const parsedRounds = useMemo(() => ensureThemeJsonArray(data?.rounds), [data]);
  const stageCount = useMemo(() => {
    const raw = Number(data?.stage_count);
    if (Number.isFinite(raw) && raw > 0) return Math.floor(raw);
    return parsedRounds.length || 1;
  }, [data, parsedRounds.length]);

  const normalizedRounds = useMemo(() => {
    return Array.from({ length: stageCount }, (_, idx) => {
      const src = parsedRounds[idx] || {};
      const dishes = ensureThemeJsonArray(src.dishes);
      const dishSlots = Math.max(
        MIN_DISHES_PER_STAGE,
        Math.min(MAX_DISHES_PER_STAGE, dishes.length || 0)
      );
      return {
        ...DEFAULT_ROUND(idx),
        ...src,
        round_id: Number(src.round_id) || idx + 1,
        title: clampText(src.title, MAX_STAGE_TITLE_LEN),
        question: clampText(src.question, MAX_STAGE_QUESTION_LEN),
        dishes: Array.from({ length: dishSlots }, (_, dishIdx) => {
          const d = { ...DEFAULT_DISH(), ...(dishes[dishIdx] || {}) };
          return { ...d, popup_text: clampText(d.popup_text, MAX_POPUP_TEXT_LEN) };
        }),
      };
    });
  }, [parsedRounds, stageCount]);

  useEffect(() => { setRoundsDraft(normalizedRounds); }, [normalizedRounds]);

  useEffect(() => {
    if (activeRoundTab > stageCount) setActiveRoundTab(stageCount);
  }, [activeRoundTab, stageCount]);

  const updateRoundField = (roundIdx, key, value) =>
    setRoundsDraft((prev) =>
      prev.map((round, idx) => (idx === roundIdx ? { ...round, [key]: value } : round))
    );

  const updateDishField = (roundIdx, dishIdx, key, value) =>
    setRoundsDraft((prev) =>
      prev.map((round, idx) => {
        if (idx !== roundIdx) return round;
        return {
          ...round,
          dishes: round.dishes.map((dish, dIdx) =>
            dIdx === dishIdx ? { ...dish, [key]: value } : dish
          ),
        };
      })
    );

  const addDish = (roundIdx) =>
    setRoundsDraft((prev) =>
      prev.map((round, idx) => {
        if (idx !== roundIdx) return round;
        const dishes = ensureThemeJsonArray(round.dishes);
        if (dishes.length >= MAX_DISHES_PER_STAGE) {
          Swal.fire("Limit reached", `Maximum ${MAX_DISHES_PER_STAGE} dishes allowed per stage.`, "warning");
          return round;
        }
        return { ...round, dishes: [...dishes, DEFAULT_DISH()] };
      })
    );

  const deleteDish = (roundIdx, dishIdx) =>
    setRoundsDraft((prev) =>
      prev.map((round, idx) => {
        if (idx !== roundIdx) return round;
        const current = ensureThemeJsonArray(round.dishes);
        if (current.length <= MIN_DISHES_PER_STAGE) {
          Swal.fire("Minimum required", `Minimum ${MIN_DISHES_PER_STAGE} dishes required per stage.`, "warning");
          return round;
        }
        return { ...round, dishes: current.filter((_, i) => i !== dishIdx) };
      })
    );

  const uploadRoundAsset = async (file) => {
    if (!file) throw new Error("Choose an image first.");
    if (!isPngFile(file)) throw new Error("Only PNG allowed.");
    const safeName = `round_asset_${Date.now()}.png`;
    const body = await postUpdateThemeMedia(pngFileWithBasename(file, safeName), "tmp_round_asset", { uploadOnly: true });
    const uploadedName = body?.uploaded_files?.tmp_round_asset;
    if (!uploadedName) throw new Error("Upload succeeded but filename is missing.");
    return uploadedName;
  };

  const handleRoundImageUpload = async (roundIdx, dishIdx, key, file, clearFileInput) => {
    if (!file) return;
    try {
      setUploadBusy(true);
      const check = await validateThemeImage(file, key);
      if (!check.ok) {
        swalInvalidImage(check.text);
        clearFileInput?.();
        return;
      }
      const uploadedName = await uploadRoundAsset(file);
      const nextRounds = roundsDraft.map((round, idx) => {
        if (idx !== roundIdx) return round;
        if (typeof dishIdx === "number") {
          return {
            ...round,
            dishes: ensureThemeJsonArray(round.dishes).map((dish, dIdx) =>
              dIdx === dishIdx ? { ...dish, [key]: uploadedName } : dish
            ),
          };
        }
        return { ...round, [key]: uploadedName };
      });
      setRoundsDraft(nextRounds);
      await updateThemeData({
        payload: {
          data: {
            stage_count: stageCount,
            rounds: nextRounds.map((round, idx) => ({
              ...round,
              round_id: idx + 1,
              dishes: ensureThemeJsonArray(round.dishes).slice(0, MAX_DISHES_PER_STAGE),
            })),
          },
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        },
        token: adminToken,
      });
      await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();
      Swal.fire("Saved", "Image uploaded and saved", "success");
    } catch (e) {
      Swal.fire("Error", e.message || "Upload failed", "error");
    } finally {
      setUploadBusy(false);
      clearFileInput?.();
    }
  };

  const saveRounds = async () => {
    const payloadRounds = roundsDraft.map((round, idx) => ({
      ...round,
      round_id: idx + 1,
      dishes: ensureThemeJsonArray(round.dishes).slice(0, MAX_DISHES_PER_STAGE),
    }));

    const emptyFieldError = (() => {
      for (let i = 0; i < payloadRounds.length; i++) {
        const round = payloadRounds[i];
        const stageNo = i + 1;
        if (!String(round.title || "").trim()) {
          return `Stage ${stageNo}: Stage Title cannot be empty.`;
        }
        if (!String(round.question || "").trim()) {
          return `Stage ${stageNo}: Stage Question cannot be empty.`;
        }
        const dishes = ensureThemeJsonArray(round.dishes);
        for (let j = 0; j < dishes.length; j++) {
          const dish = dishes[j] || {};
          const dishNo = j + 1;
          if (!String(dish.dish_name || "").trim()) {
            return `Stage ${stageNo}, Dish ${dishNo}: Dish Name cannot be empty.`;
          }
          if (!String(dish.dish_category_name || "").trim()) {
            return `Stage ${stageNo}, Dish ${dishNo}: Category cannot be empty.`;
          }
          if (
            String(dish.type || "").toLowerCase() === "negative" &&
            !String(dish.popup_text || "").trim()
          ) {
            return `Stage ${stageNo}, Dish ${dishNo}: Popup Text cannot be empty for negative dish.`;
          }
        }
      }
      return "";
    })();

    if (emptyFieldError) {
      await Swal.fire({
        icon: "warning",
        title: "Validation error",
        text: emptyFieldError,
      });
      return;
    }

    setLoading(true);
    try {
      await updateThemeData({
        payload: {
          data: { stage_count: stageCount, rounds: payloadRounds },
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        },
        token: adminToken,
      });
      await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();
      Swal.fire("Saved", "Rounds updated successfully", "success");
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to save rounds", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleGameImageUpload = async (fieldKey, file, clearFileInput) => {
    if (!file) return;
    try {
      setUploadBusy(true);
      const check = await validateThemeImage(file, fieldKey);
      if (!check.ok) {
        swalInvalidImage(check.text);
        clearFileInput?.();
        return;
      }
      const uploadedName = await uploadRoundAsset(file);
      await updateThemeData({
        payload: {
          data: { [fieldKey]: uploadedName },
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        },
        token: adminToken,
      });
      await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();
      Swal.fire("Saved", `${fieldKey} updated successfully`, "success");
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to update image", "error");
    } finally {
      setUploadBusy(false);
      clearFileInput?.();
    }
  };

  if (status === "loading" && !data) {
    return (
      <div className="themeupdate-page">
        <div className="themeupdate-loading">Loading theme data…</div>
      </div>
    );
  }

  /* ─── Stage editor: two-column layout ─── */
  const renderRoundEditor = (round, roundIdx) => (
    <div className="themeupdate-feedback-card">
      <div className="themeupdate-card-header">Stage {roundIdx + 1} — Details &amp; Dishes</div>

      <div className="themeupdate-card-body">
        {/* TWO-COLUMN MASTER GRID */}
        <div className="stage-editor-grid">

          {/* ── LEFT: stage meta ── */}
          <div className="stage-meta-panel">
            <div className="stage-meta-panel-header">Stage Info</div>
            <div className="stage-meta-body">

              <div className="field-group">
                <label className="themeupdate-label">Stage Title</label>
                <input
                  type="text"
                  className="themeupdate-input"
                  placeholder="Enter stage title…"
                  value={round.title || ""}
                  maxLength={MAX_STAGE_TITLE_LEN}
                  onChange={(e) =>
                    updateRoundField(roundIdx, "title", clampText(e.target.value, MAX_STAGE_TITLE_LEN))
                  }
                />
                <CharLimitHint max={MAX_STAGE_TITLE_LEN} />
              </div>

              <div className="field-group">
                <label className="themeupdate-label">Stage Question</label>
                <input
                  type="text"
                  className="themeupdate-input"
                  placeholder="Enter question…"
                  value={round.question || ""}
                  maxLength={MAX_STAGE_QUESTION_LEN}
                  onChange={(e) =>
                    updateRoundField(roundIdx, "question", clampText(e.target.value, MAX_STAGE_QUESTION_LEN))
                  }
                />
                <CharLimitHint max={MAX_STAGE_QUESTION_LEN} />
              </div>

              <div className="themeupdate-hr" />

              {/* Hint image */}
              <div className="image-upload-row">
                <label className="themeupdate-label">Pop up Image</label>
                <div className="image-upload-preview">
                  {round.hint_image ? (
                    <img src={getDvSourceImageUrl(round.hint_image)} alt="Hint" />
                  ) : (
                    <span className="no-img-text">No image yet</span>
                  )}
                </div>
                <ImageSpecHint fieldKey="hint_image" />
                <div className="image-upload-input-wrap">
                  <input
                    type="file"
                    accept="image/png"
                    disabled={uploadBusy}
                    onChange={(e) => {
                      const input = e.currentTarget;
                      handleRoundImageUpload(roundIdx, null, "hint_image", input.files?.[0], () => {
                        input.value = "";
                      });
                    }}
                  />
                </div>
                <span className="upload-tip">Saves immediately on select</span>
              </div>

              {/* Required recipe image */}
              <div className="image-upload-row">
                <label className="themeupdate-label">Required Recipe Image</label>
                <div className="image-upload-preview">
                  {round.required_recipe_image ? (
                    <img src={getDvSourceImageUrl(round.required_recipe_image)} alt="Recipe" />
                  ) : (
                    <span className="no-img-text">No image yet</span>
                  )}
                </div>
                <ImageSpecHint fieldKey="required_recipe_image" />
                <div className="image-upload-input-wrap">
                  <input
                    type="file"
                    accept="image/png"
                    disabled={uploadBusy}
                    onChange={(e) => {
                      const input = e.currentTarget;
                      handleRoundImageUpload(
                        roundIdx,
                        null,
                        "required_recipe_image",
                        input.files?.[0],
                        () => {
                          input.value = "";
                        }
                      );
                    }}
                  />
                </div>
                <span className="upload-tip">Saves immediately on select</span>
              </div>

              {/* Stage-wise SOS layered images */}
              <div className="image-upload-row">
                <label className="themeupdate-label">SOS1 Layered Image</label>
                <div className="image-upload-preview">
                  {round.sos1_layered_image ? (
                    <img src={getDvSourceImageUrl(round.sos1_layered_image)} alt="SOS1 Layered" />
                  ) : (
                    <span className="no-img-text">No image yet</span>
                  )}
                </div>
                <ImageSpecHint fieldKey="sos1_layered_image" />
                <div className="image-upload-input-wrap">
                  <input
                    type="file"
                    accept="image/png"
                    disabled={uploadBusy}
                    onChange={(e) => {
                      const input = e.currentTarget;
                      handleRoundImageUpload(
                        roundIdx,
                        null,
                        "sos1_layered_image",
                        input.files?.[0],
                        () => {
                          input.value = "";
                        }
                      );
                    }}
                  />
                </div>
                <span className="upload-tip">Saves immediately on select</span>
              </div>

              <div className="image-upload-row">
                <label className="themeupdate-label">SOS2 Layered Image</label>
                <div className="image-upload-preview">
                  {round.sos2_layered_image ? (
                    <img src={getDvSourceImageUrl(round.sos2_layered_image)} alt="SOS2 Layered" />
                  ) : (
                    <span className="no-img-text">No image yet</span>
                  )}
                </div>
                <ImageSpecHint fieldKey="sos2_layered_image" />
                <div className="image-upload-input-wrap">
                  <input
                    type="file"
                    accept="image/png"
                    disabled={uploadBusy}
                    onChange={(e) => {
                      const input = e.currentTarget;
                      handleRoundImageUpload(
                        roundIdx,
                        null,
                        "sos2_layered_image",
                        input.files?.[0],
                        () => {
                          input.value = "";
                        }
                      );
                    }}
                  />
                </div>
                <span className="upload-tip">Saves immediately on select</span>
              </div>

              <div className="image-upload-row">
                <label className="themeupdate-label">SOS3 Layered Image</label>
                <div className="image-upload-preview">
                  {round.sos3_layered_image ? (
                    <img src={getDvSourceImageUrl(round.sos3_layered_image)} alt="SOS3 Layered" />
                  ) : (
                    <span className="no-img-text">No image yet</span>
                  )}
                </div>
                <ImageSpecHint fieldKey="sos3_layered_image" />
                <div className="image-upload-input-wrap">
                  <input
                    type="file"
                    accept="image/png"
                    disabled={uploadBusy}
                    onChange={(e) => {
                      const input = e.currentTarget;
                      handleRoundImageUpload(
                        roundIdx,
                        null,
                        "sos3_layered_image",
                        input.files?.[0],
                        () => {
                          input.value = "";
                        }
                      );
                    }}
                  />
                </div>
                <span className="upload-tip">Saves immediately on select</span>
              </div>

            </div>
          </div>

          {/* ── RIGHT: dishes ── */}
          <div className="stage-dishes-panel">
            <div className="stage-dishes-header">
              <div>
                <div className="stage-dishes-title">Dishes (Ingredients)</div>
                <div className="stage-dishes-meta">
                  {round.dishes.length}/{MAX_DISHES_PER_STAGE} dishes &nbsp;·&nbsp; min {MIN_DISHES_PER_STAGE}, max {MAX_DISHES_PER_STAGE}
                </div>
              </div>
              <button
                type="button"
                className="themeupdate-btn themeupdate-btn-primary"
                onClick={() => addDish(roundIdx)}
                disabled={round.dishes.length >= MAX_DISHES_PER_STAGE || uploadBusy}
              >
                + Add Dish
              </button>
            </div>

            <div className="themeupdate-round-dishes">
              {round.dishes.map((dish, dishIdx) => (
                <div key={`${roundIdx}-${dishIdx}`} className="themeupdate-round-dish-card">
                  {/* Card header */}
                  <div className="themeupdate-round-dish-head">
                    <span className="dish-number-badge">{dishIdx + 1}</span>
                    <span className="dish-card-label">Dish {dishIdx + 1}</span>
                    <button
                      type="button"
                      className="themeupdate-btn themeupdate-btn-danger themeupdate-btn-sm"
                      onClick={() => deleteDish(roundIdx, dishIdx)}
                      disabled={round.dishes.length <= MIN_DISHES_PER_STAGE || uploadBusy}
                    >
                      ✕ Remove
                    </button>
                  </div>

                  {/* Name + Category in a row */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                    <div className="field-group">
                      <label className="themeupdate-label">Dish Name</label>
                      <input
                        type="text"
                        className="themeupdate-input"
                        placeholder="dish_name"
                        value={dish.dish_name || ""}
                        maxLength={MAX_DISH_TEXT_LEN}
                        onChange={(e) =>
                          updateDishField(
                            roundIdx,
                            dishIdx,
                            "dish_name",
                            clampText(e.target.value, MAX_DISH_TEXT_LEN)
                          )
                        }
                      />
                      <CharLimitHint max={MAX_DISH_TEXT_LEN} />
                    </div>
                    <div className="field-group">
                      <label className="themeupdate-label">Category</label>
                      <input
                        type="text"
                        className="themeupdate-input"
                        placeholder="category"
                        value={dish.dish_category_name || ""}
                        maxLength={MAX_DISH_TEXT_LEN}
                        onChange={(e) =>
                          updateDishField(
                            roundIdx,
                            dishIdx,
                            "dish_category_name",
                            clampText(e.target.value, MAX_DISH_TEXT_LEN)
                          )
                        }
                      />
                      <CharLimitHint max={MAX_DISH_TEXT_LEN} />
                    </div>
                  </div>

                  {/* Type */}
                  <div className="field-group">
                    <label className="themeupdate-label">Type</label>
                    <select
                      className="themeupdate-input"
                      value={dish.type || "positive"}
                      onChange={(e) => updateDishField(roundIdx, dishIdx, "type", e.target.value)}
                    >
                      <option value="positive">✅ Positive</option>
                      <option value="negative">❌ Negative</option>
                    </select>
                  </div>

                  {/* Images — two side by side */}
                  <div className="dish-images-row">
                    <ImageSlot
                      label="Dish Image"
                      fieldKey="dish_img"
                      src={dish.dish_img}
                      busy={uploadBusy}
                      onFileChange={(f, clearInput) =>
                        handleRoundImageUpload(roundIdx, dishIdx, "dish_img", f, clearInput)
                      }
                    />
                    {dish.type !== "negative" ? (
                      <ImageSlot
                        label="Layered Image"
                        fieldKey="dish_layered_img"
                        src={dish.dish_layered_img}
                        busy={uploadBusy}
                        onFileChange={(f, clearInput) =>
                          handleRoundImageUpload(roundIdx, dishIdx, "dish_layered_img", f, clearInput)
                        }
                      />
                    ) : null}
                  </div>

                  {/* Popup text (only for negative dishes) */}
                  {dish.type === "negative" ? (
                    <div className="field-group popup-text-field">
                      <label className="themeupdate-label">Popup Text</label>
                      <input
                        type="text"
                        className="themeupdate-input"
                        placeholder="popup text"
                        value={dish.popup_text || ""}
                        maxLength={MAX_POPUP_TEXT_LEN}
                        onChange={(e) =>
                          updateDishField(
                            roundIdx,
                            dishIdx,
                            "popup_text",
                            clampText(e.target.value, MAX_POPUP_TEXT_LEN)
                          )
                        }
                      />
                      <CharLimitHint max={MAX_POPUP_TEXT_LEN} />
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Save bar */}
        <div className="themeupdate-actions">
          {uploadBusy && (
            <span className="themeupdate-muted" style={{ marginRight: "auto" }}>
              <code>Uploading image…</code>
            </span>
          )}
          <button
            type="button"
            className="themeupdate-btn themeupdate-btn-success"
            onClick={saveRounds}
            disabled={loading || uploadBusy}
          >
            {loading ? "Saving…" : "💾 Save Stage Rounds"}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="themeupdate-page">
      {/* Back */}
      <div className="back-button-holder">
        <button type="button" className="back-button" onClick={() => navigate("/admin/rules")}>
          <i className="fa-solid fa-arrow-left" /> Back
        </button>
      </div>

      {/* Top tab */}
      <div className="themeupdate-tabs">
        <button
          type="button"
          className={`themeupdate-tab ${activeTopTab === "stageRounds" ? "active" : ""}`}
          onClick={() => setActiveTopTab("stageRounds")}
        >
          <i className="fa-solid fa-layer-group" /> Stage Rounds
        </button>
        <button
          type="button"
          className={`themeupdate-tab ${activeTopTab === "gameImages" ? "active" : ""}`}
          onClick={() => setActiveTopTab("gameImages")}
        >
          <i className="fa-solid fa-image" /> Game Images
        </button>
      </div>

      {/* Main panel */}
      <div className="themeupdate-panel-wide">
        {activeTopTab === "stageRounds" ? (
          <div className="themeupdate-card">
            <div className="themeupdate-card-header">Stage Rounds Editor</div>
            <div className="themeupdate-card-body">
              <div className="stage-count-badge">
                auto-generated from stage_count: <strong>{stageCount}</strong>
              </div>

              {/* Stage tabs */}
              <div className="themeupdate-round-tabs">
                {Array.from({ length: stageCount }, (_, idx) => (
                  <button
                    key={idx + 1}
                    type="button"
                    className={`themeupdate-tab ${activeRoundTab === idx + 1 ? "active" : ""}`}
                    onClick={() => setActiveRoundTab(idx + 1)}
                  >
                    Stage {idx + 1}
                  </button>
                ))}
              </div>

              {roundsDraft[activeRoundTab - 1]
                ? renderRoundEditor(roundsDraft[activeRoundTab - 1], activeRoundTab - 1)
                : null}
            </div>
          </div>
        ) : (
          <div className="themeupdate-card">
            <div className="themeupdate-card-header">Game Images</div>
            <div className="themeupdate-card-body">
              <div className="dish-images-row game-images-row">
                <ImageSlot
                  label="Plate Image"
                  fieldKey="plate_image"
                  src={data?.plate_image}
                  busy={uploadBusy}
                  compact
                  onFileChange={(f, clearInput) => handleGameImageUpload("plate_image", f, clearInput)}
                />
                <ImageSlot
                  label="Bowl Image"
                  fieldKey="bowl_image"
                  src={data?.bowl_image}
                  busy={uploadBusy}
                  compact
                  onFileChange={(f, clearInput) => handleGameImageUpload("bowl_image", f, clearInput)}
                />
                <ImageSlot
                  label="Table Image"
                  fieldKey="table_image"
                  src={data?.table_image}
                  busy={uploadBusy}
                  compact
                  onFileChange={(f, clearInput) => handleGameImageUpload("table_image", f, clearInput)}
                />
                <ImageSlot
                  label="SOS1 Image"
                  fieldKey="sos1_image"
                  src={data?.sos1_image}
                  busy={uploadBusy}
                  compact
                  onFileChange={(f, clearInput) => handleGameImageUpload("sos1_image", f, clearInput)}
                />
                <ImageSlot
                  label="SOS2 Image"
                  fieldKey="sos2_image"
                  src={data?.sos2_image}
                  busy={uploadBusy}
                  compact
                  onFileChange={(f, clearInput) => handleGameImageUpload("sos2_image", f, clearInput)}
                />
                <ImageSlot
                  label="SOS3 Image"
                  fieldKey="sos3_image"
                  src={data?.sos3_image}
                  busy={uploadBusy}
                  compact
                  onFileChange={(f, clearInput) => handleGameImageUpload("sos3_image", f, clearInput)}
                />
              </div>
              {uploadBusy && (
                <div className="themeupdate-actions">
                  <span className="themeupdate-muted">
                    <code>Uploading image…</code>
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ThemeUpdate;
